import { useEffect, useMemo } from 'react';
import { createStore, createHook, createActionsHook } from 'react-sweet-state';

import { cfetch } from '@confluence/network';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';

import { formatTapTraits, type TapTraitAttribute } from './formatTapTraits.ts';

export type AtlassianAccountTraits = {
	attributes: TapTraitAttribute[] | null;
};

export type AtlassianAccountTraitsState = {
	data: AtlassianAccountTraits | null;
	loading: boolean;
	error: Error | null;
};

const initialState: AtlassianAccountTraitsState = {
	data: null,
	loading: false,
	error: null,
};

const actions = {
	start:
		() =>
		({ setState }: { setState: (s: Partial<AtlassianAccountTraitsState>) => void }) => {
			setState({ loading: true, error: null });
		},
	success:
		(data: AtlassianAccountTraits) =>
		({ setState }: { setState: (s: Partial<AtlassianAccountTraitsState>) => void }) => {
			setState({ data, loading: false, error: null });
		},
	error:
		(err: Error) =>
		({ setState }: { setState: (s: Partial<AtlassianAccountTraitsState>) => void }) => {
			setState({ error: err, loading: false });
		},
	reset:
		() =>
		({ setState }: { setState: (s: Partial<AtlassianAccountTraitsState>) => void }) => {
			setState({ data: null, loading: false, error: null });
		},
};

export const atlassianAccountTraitsStore = createStore({
	initialState,
	actions,
	name: 'atlassianAccountTraitsStore',
});

export const useAtlassianAccountTraitsStore = createHook(atlassianAccountTraitsStore);
export const useAtlassianAccountTraitsActions = createActionsHook(atlassianAccountTraitsStore);

async function fetchAtlassianAccountTraitsData(userId: string): Promise<AtlassianAccountTraits> {
	const res = await cfetch(`/gateway/api/tap-delivery/api/v3/personalization/user/${userId}`, {
		method: 'GET',
	});
	if (!res.ok) {
		const text = await res.text().catch(() => '');
		throw new Error(`Request failed with ${res.status}: ${text || res.statusText}`);
	}
	return (await res.json()) as AtlassianAccountTraits;
}

// Use a module-level variable to ensure we only fetch once per app lifecycle
// This way multiple components using the hook won't trigger multiple fetches
let hasFetched: boolean = false;

// Test-only utility function to reset the hasFetched flag
export function resetHasFetched() {
	hasFetched = false;
}

function getAtlassianAccountTraitsFromSources(): AtlassianAccountTraits | null {
	let attributes: TapTraitAttribute[] | null = null;
	// First check window.__SSR_TAP_CONTEXT__ which contains raw tapContext data
	if (typeof window !== 'undefined' && window.__SSR_TAP_CONTEXT__) {
		attributes = formatTapTraits(window.__SSR_TAP_CONTEXT__.user);
		if (attributes) {
			return { attributes };
		}
	}

	// Fallback to getSSRRenderInputs() for SSR scenarios
	const { tapContext } = getSSRRenderInputs();
	attributes = formatTapTraits(tapContext?.user);
	if (attributes) {
		return { attributes };
	}

	return null;
}

export function useAtlassianAccountTraits({ skipBootstrap }: { skipBootstrap?: boolean } = {}): {
	data: AtlassianAccountTraits | null;
	loading: boolean;
	error: Error | null;
} {
	const preloadedData = useMemo(() => getAtlassianAccountTraitsFromSources(), []);

	const [{ data, loading, error }, storeActions] = useAtlassianAccountTraitsStore();
	const { accessStatus, userId } = useSessionData();
	const hasLicensedAccess =
		accessStatus === 'LICENSED_USE_ACCESS' || accessStatus === 'LICENSED_ADMIN_ACCESS';

	useEffect(() => {
		if (preloadedData || data || loading || error) return;

		if (hasFetched || skipBootstrap) return;
		if (!hasLicensedAccess || !userId) return;

		hasFetched = true;
		storeActions.start();
		fetchAtlassianAccountTraitsData(userId).then(storeActions.success).catch(storeActions.error);
	}, [skipBootstrap, preloadedData, data, loading, error, storeActions, hasLicensedAccess, userId]);

	if (preloadedData) {
		return { data: preloadedData, loading: false, error: null };
	}

	return { data, loading, error };
}
